import { useEffect, useMemo, useRef, useState } from "react";
import { APP_ERROR_EVENT, notifyUnexpectedError, type AppErrorNotice } from "../errors";
import { Button, Icon } from "../ui";

const MAX_VISIBLE = 3;
const DEDUPE_WINDOW_MS = 8000;

function displayTime(iso: string): string {
  return new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(new Date(iso));
}

export function GlobalErrorLayer() {
  const [items, setItems] = useState<AppErrorNotice[]>([]);
  const lastSeenRef = useRef<Record<string, number>>({});

  useEffect(() => {
    const onAppError = (event: Event) => {
      const notice = (event as CustomEvent<AppErrorNotice>).detail;
      if (!notice) return;
      const now = Date.now();
      const last = lastSeenRef.current[notice.fingerprint] ?? 0;
      if (now - last < DEDUPE_WINDOW_MS) return;
      lastSeenRef.current = { ...lastSeenRef.current, [notice.fingerprint]: now };
      setItems((current) => [notice, ...current].slice(0, MAX_VISIBLE));
    };
    const onWindowError = (event: ErrorEvent) => {
      notifyUnexpectedError(event.error ?? event.message, "window.error");
    };
    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      notifyUnexpectedError(event.reason, "unhandledrejection");
    };

    window.addEventListener(APP_ERROR_EVENT, onAppError);
    window.addEventListener("error", onWindowError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);
    return () => {
      window.removeEventListener(APP_ERROR_EVENT, onAppError);
      window.removeEventListener("error", onWindowError);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, []);

  const ordered = useMemo(() => items.slice(0, MAX_VISIBLE), [items]);
  if (!ordered.length) return null;

  return (
    <div
      className="fixed z-[80] bottom-4 right-4 left-4 md:left-auto md:w-[28rem] space-y-3"
      aria-live="assertive"
    >
      {ordered.map((item) => (
        <div
          key={item.id}
          role="alert"
          className="bg-[var(--paper)] border-2 border-[var(--strike)] shadow-[0_16px_40px_-24px_rgba(0,0,0,0.45)]"
        >
          <div className="flex items-start gap-3 p-4">
            <Icon.AlertCircle
              size={18}
              strokeWidth={2.25}
              className="mt-0.5 shrink-0 text-[var(--strike)]"
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-3">
                <div className="label text-[var(--strike)]">{item.title}</div>
                <div className="caption shrink-0">{displayTime(item.timestamp)}</div>
              </div>
              <div className="body mt-1 text-[13px] break-words">{item.message}</div>
              {(item.status || item.detail) && (
                <div className="caption mt-2 break-words">
                  {[item.status ? `HTTP ${item.status}` : null, item.detail]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              )}
            </div>
            <button
              type="button"
              className="p-1 text-[var(--ink-3)] hover:text-[var(--ink)] shrink-0"
              aria-label="Fechar erro"
              onClick={() => {
                setItems((current) => current.filter((currentItem) => currentItem.id !== item.id));
              }}
            >
              <Icon.X size={16} strokeWidth={2} />
            </button>
          </div>
        </div>
      ))}
      <div className="flex justify-end">
        <Button
          variant="paper"
          size="xs"
          onClick={() => {
            setItems([]);
          }}
        >
          Limpar avisos
        </Button>
      </div>
    </div>
  );
}
